import { useTranslation } from 'react-i18next';
import { ClickAnchor, ElementsButton, ClickMenuContainer, Icon } from '../../../components';
import { useFeatureFlagsContext } from '../../../../FeatureFlags';
import { usePresentationViewOptionsContext } from '../context';
import { PresentationViewOptionListItem } from './PresentationViewOptionListItem';
import { PresentationViewOptionsQuickActions } from './PresentationViewOptionsQuickActions';
import { elementShortcutMap, otherShortcutMap } from '../util';
import { useRef, useState } from 'react';
import { useClickAway, useKeyPressEvent } from 'react-use';

export function PresentationViewOptions() {
  const {
    viewOptions,
    toggleShowClientAge,
    toggleShowClientName,
    toggleElementVisibility,
    toggleShowFinancialDetails,
    toggleShowEmojis,
    toggleShowTargetScores,
  } = usePresentationViewOptionsContext();
  const { t: tPages } = useTranslation('pages');
  const { t: tScorecard } = useTranslation('scorecard');
  const { allowEstateStatus } = useFeatureFlagsContext();
  const showEstateStatus = allowEstateStatus === true;

  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  useKeyPressEvent('Escape', () => setViewOptionsOpen(false));
  const viewOptionsRef = useRef<HTMLDivElement>(null);
  useClickAway(viewOptionsRef, () => setViewOptionsOpen(false));

  return (
    <ClickAnchor
      placement="bottom-end"
      popperJSPreventOverflowOptions={{ altAxis: true, padding: 24 }}
      floatingElement={
        viewOptionsOpen ? (
          <div ref={viewOptionsRef}>
            <ClickMenuContainer className="!w-[300px] overflow-y-scroll max-h-[calc(100vh_-_48px)]">
              <PresentationViewOptionsQuickActions />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Sr'].toUpperCase()}
                label={tScorecard('savings-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="srFinancialHealth" />}
                onClick={() => toggleElementVisibility('Sr')}
                icon={viewOptions?.focusedElements?.includes('Sr') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Br'].toUpperCase()}
                label={tScorecard('burn-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="brFinancialHealth" />}
                onClick={() => toggleElementVisibility('Br')}
                icon={viewOptions?.focusedElements?.includes('Br') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Dr'].toUpperCase()}
                label={tScorecard('debt-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="drFinancialHealth" />}
                onClick={() => toggleElementVisibility('Dr')}
                icon={viewOptions?.focusedElements?.includes('Dr') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Tr'].toUpperCase()}
                label={tScorecard('tax-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="trFinancialHealth" />}
                onClick={() => toggleElementVisibility('Tr')}
                icon={viewOptions?.focusedElements?.includes('Tr') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Lt'].toUpperCase()}
                label={tScorecard('liquid-term')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="ltFinancialHealth" />}
                onClick={() => toggleElementVisibility('Lt')}
                icon={viewOptions?.focusedElements?.includes('Lt') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Qt'].toUpperCase()}
                label={tScorecard('qualified-term')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="qtFinancialHealth" />}
                onClick={() => toggleElementVisibility('Qt')}
                icon={viewOptions?.focusedElements?.includes('Qt') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Rt'].toUpperCase()}
                label={tScorecard('real-estate-term')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="rtFinancialHealth" />}
                onClick={() => toggleElementVisibility('Rt')}
                icon={viewOptions?.focusedElements?.includes('Rt') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Bt'].toUpperCase()}
                label={tScorecard('business-term')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="btFinancialHealth" />}
                onClick={() => toggleElementVisibility('Bt')}
                icon={viewOptions?.focusedElements?.includes('Bt') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Tt'].toUpperCase()}
                label={tScorecard('total-term')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="ttFinancialHealth" />}
                onClick={() => toggleElementVisibility('Tt')}
                icon={viewOptions?.focusedElements?.includes('Tt') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Er'].toUpperCase()}
                label={tScorecard('equity-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="erFinancialHealth" />}
                onClick={() => toggleElementVisibility('Er')}
                icon={viewOptions?.focusedElements?.includes('Er') && 'check'}
              />
              <PresentationViewOptionListItem
                shortcutCharacter={elementShortcutMap['Ir'].toUpperCase()}
                label={tScorecard('insurance-rate')}
                prefix={<Icon className="mr-3 !h-6 !w-6" icon="irFinancialHealth" />}
                onClick={() => toggleElementVisibility('Ir')}
                icon={viewOptions?.focusedElements?.includes('Ir') && 'check'}
              />
              {showEstateStatus ? (
                <PresentationViewOptionListItem
                  shortcutCharacter={elementShortcutMap['Es'].toUpperCase()}
                  label={tScorecard('estate-status')}
                  prefix={<Icon className="mr-3 !h-6 !w-6" icon="esFinancialHealth" />}
                  onClick={() => toggleElementVisibility('Es')}
                  icon={viewOptions?.focusedElements?.includes('Es') && 'check'}
                />
              ) : null}
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleEmojis'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowEmojis}
                icon={viewOptions?.showEmojis ? 'hide' : 'show'}
                label={
                  viewOptions?.showEmojis
                    ? tPages('full-screen-scorecard-view-options-hide-emojis')
                    : tPages('full-screen-scorecard-view-options-show-emojis')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleTargetScores'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowTargetScores}
                icon={viewOptions?.showTargetScores ? 'hide' : 'show'}
                label={
                  viewOptions?.showTargetScores
                    ? tPages('full-screen-scorecard-view-options-hide-target-ranges')
                    : tPages('full-screen-scorecard-view-options-show-target-ranges')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleClientName'].toUpperCase()}
                onClick={toggleShowClientName}
                className="[&_p]:font-medium"
                icon={viewOptions?.showClientName ? 'hide' : 'show'}
                label={
                  viewOptions?.showClientName
                    ? tPages('full-screen-scorecard-view-options-hide-client-name')
                    : tPages('full-screen-scorecard-view-options-show-client-name')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleClientAge'].toUpperCase()}
                onClick={toggleShowClientAge}
                className="[&_p]:font-medium"
                icon={viewOptions?.showClientAge ? 'hide' : 'show'}
                label={
                  viewOptions?.showClientAge
                    ? tPages('full-screen-scorecard-view-options-hide-client-age')
                    : tPages('full-screen-scorecard-view-options-show-client-age')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleFinancialDetails'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowFinancialDetails}
                icon={viewOptions?.showFinancialDetails ? 'hide' : 'show'}
                label={
                  viewOptions?.showFinancialDetails
                    ? tPages('full-screen-scorecard-view-options-hide-financial-details')
                    : tPages('full-screen-scorecard-view-options-show-financial-details')
                }
              />
            </ClickMenuContainer>
          </div>
        ) : (
          <ClickMenuContainer></ClickMenuContainer>
        )
      }
    >
      <ElementsButton
        size="small"
        theme="text"
        icon="arrowDropdown"
        label={tPages('full-screen-scorecard-view-options-button')}
        onClick={() => setViewOptionsOpen(!viewOptionsOpen)}
      />
    </ClickAnchor>
  );
}
